import React from 'react';
import { Page, View, Text, StyleSheet, Image, Svg, Path } from '@react-pdf/renderer';
import { ReportData } from '../../types/report.types';
import { getAttestationData } from '../../utils/attestationUtils';
import { CTB_LOGO_DARK_BASE64 } from '../../../../assets/ctbLogoDarkBase64';

interface LetterOfAttestationPDFProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    size: 'A4',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 44,
    paddingBottom: 40,
    paddingLeft: 48,
    paddingRight: 48,
    fontFamily: 'Roboto',
    fontSize: 10.2,
    color: '#334155',
    lineHeight: 1.55,
    position: 'relative',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
  },
  logo: {
    width: 160,
    height: 48,
    objectFit: 'contain',
  },
  addressBlock: {
    textAlign: 'right',
    alignItems: 'flex-end',
  },
  companyNameHeader: {
    fontSize: 9.8,
    fontWeight: 'bold',
    color: '#0F172A',
    marginBottom: 2.5,
  },
  addressLine: {
    fontSize: 8,
    color: '#64748B',
    lineHeight: 1.35,
  },
  websiteLink: {
    fontSize: 8.2,
    color: '#007BFC',
    fontWeight: 'bold',
    marginTop: 2,
  },
  divider: {
    borderBottomWidth: 1.5,
    borderBottomColor: '#007BFC',
    width: '100%',
    marginTop: 13,
    marginBottom: 15,
  },
  titleBlock: {
    marginBottom: 13,
  },
  mainTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#00238C',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  subTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#007BFC',
    marginTop: 3.5,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 13,
  },
  metaText: {
    fontSize: 10,
    color: '#1E293B',
  },
  metaLabel: {
    fontWeight: 'bold',
    color: '#0F172A',
  },
  salutation: {
    fontSize: 10.2,
    color: '#0F172A',
    marginBottom: 10,
  },
  paragraph: {
    fontSize: 10.2,
    color: '#334155',
    lineHeight: 1.55,
    marginBottom: 11,
  },
  boldText: {
    fontWeight: 'bold',
    color: '#0F172A',
  },
  // Scope / Details List - Clean unbordered 2-column layout matching the mockup
  detailsList: {
    width: '100%',
    marginTop: 2,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 4.5,
    alignItems: 'flex-start',
  },
  detailLabel: {
    width: 118,
    fontSize: 10.2,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  detailValue: {
    flex: 1,
    fontSize: 10.2,
    color: '#1E293B',
    lineHeight: 1.45,
  },
  // Findings severity inline counts
  critBadge: {
    fontWeight: 'bold',
    color: '#DC2626',
  },
  highBadge: {
    fontWeight: 'bold',
    color: '#EA580C',
  },
  medBadge: {
    fontWeight: 'bold',
    color: '#D97706',
  },
  lowBadge: {
    fontWeight: 'bold',
    color: '#16A34A',
  },
  // Remediation Verified Callout Block - Uniform 1px green border
  remediationBlock: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#22C55E',
    borderRadius: 4,
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 10,
    paddingRight: 10,
    marginTop: 2,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  remediationText: {
    fontSize: 9.8,
    color: '#14532D',
    lineHeight: 1.48,
    flex: 1,
  },
  remediationBold: {
    fontWeight: 'bold',
    color: '#15803D',
  },
  contactEmail: {
    color: '#007BFC',
    fontWeight: 'bold',
  },
  signoffBlock: {
    marginTop: 4,
    marginBottom: 16,
  },
  signoffTitle: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  signoffSubtitle: {
    fontSize: 8.8,
    fontStyle: 'italic',
    color: '#64748B',
    marginTop: 2.5,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 48,
    right: 48,
  },
  footerDivider: {
    borderBottomWidth: 0.8,
    borderBottomColor: '#CBD5E1',
    width: '100%',
    marginBottom: 6,
  },
  footerText: {
    fontSize: 7.8,
    color: '#94A3B8',
    textAlign: 'center',
  },
});

export const LetterOfAttestationPDF: React.FC<LetterOfAttestationPDFProps> = ({
  reportData,
  sectionId
}) => {
  const data = getAttestationData(reportData);

  const detailRows = [
    { label: 'Client', value: data.client_name },
    { label: 'Assessment type', value: data.assessment_type },
    { label: 'Approach', value: data.approach },
    { label: 'Scope', value: data.scope },
    { label: 'Testing window', value: data.testing_window },
  ];

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Header with Dark Logo on Left, Address on Right */}
      <View style={styles.headerRow}>
        <Image src={CTB_LOGO_DARK_BASE64} style={styles.logo} />
        <View style={styles.addressBlock}>
          <Text style={styles.companyNameHeader}>Capture The Bug Limited</Text>
          <Text style={styles.addressLine}>Newmarket Innovation Precinct, Level 2 B903,</Text>
          <Text style={styles.addressLine}>298 Khyber Pass Road, Newmarket, Auckland 1023</Text>
          <Text style={styles.websiteLink}>capturethebug.xyz</Text>
        </View>
      </View>

      {/* Horizontal CTB Blue Divider Line */}
      <View style={styles.divider} />

      {/* Document Titles */}
      <View style={styles.titleBlock}>
        <Text style={styles.mainTitle}>LETTER OF ATTESTATION</Text>
        <Text style={styles.subTitle}>Penetration Testing Engagement</Text>
      </View>

      {/* Date and Reference Row */}
      <View style={styles.metaRow}>
        <Text style={styles.metaText}>
          <Text style={styles.metaLabel}>Date: </Text>
          {data.date}
        </Text>
        <Text style={styles.metaText}>
          <Text style={styles.metaLabel}>Reference: </Text>
          {data.reference}
        </Text>
      </View>

      {/* Salutation */}
      <Text style={styles.salutation}>To whom it may concern,</Text>

      {/* Confirmation Paragraph */}
      <Text style={styles.paragraph}>
        This letter confirms that <Text style={styles.boldText}>Capture The Bug Limited</Text>, a CREST-certified penetration testing provider, was engaged by <Text style={styles.boldText}>{data.client_name}</Text> to perform an independent penetration test of the systems described below.
      </Text>

      {/* Scope / Details List - Clean unbordered 2 columns matching mockup */}
      <View style={styles.detailsList}>
        {detailRows.map((row, idx) => (
          <View key={idx} style={styles.detailRow}>
            <Text style={styles.detailLabel}>{row.label}</Text>
            <Text style={styles.detailValue}>{row.value}</Text>
          </View>
        ))}
      </View>

      {/* Methodology Paragraph */}
      <Text style={styles.paragraph}>
        The assessment was conducted in line with industry-recognised methodologies, including the OWASP Testing Guides and the OWASP Top 10 (2021), as applicable to the engagement. Identified issues were manually validated and rated for severity. The detailed technical findings and remediation guidance were delivered to <Text style={styles.boldText}>{data.client_name}</Text> in a separate confidential report dated <Text style={styles.boldText}>{data.report_date}</Text>.
      </Text>

      {/* Findings Count Paragraph with Colored Accents */}
      <Text style={styles.paragraph}>
        During the engagement, Capture The Bug identified <Text style={styles.boldText}>{data.total_findings}</Text> issues across the in-scope assets (<Text style={styles.critBadge}>{data.critical_count} Critical</Text>, <Text style={styles.highBadge}>{data.high_count} High</Text>, <Text style={styles.medBadge}>{data.medium_count} Medium</Text>, <Text style={styles.lowBadge}>{data.low_count} Low</Text>). These findings were reported to <Text style={styles.boldText}>{data.client_name}</Text> for remediation.
      </Text>

      {/* Remediation Verified Block (Optional - only shown when verification retest completed) */}
      {data.remediation_verified && (
        <View style={styles.remediationBlock}>
          <Svg viewBox="0 0 24 24" style={{ width: 12, height: 12, marginRight: 6, marginTop: 1 }}>
            <Path
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
              fill="#16A34A"
            />
          </Svg>
          <Text style={styles.remediationText}>
            <Text style={styles.remediationBold}>Remediation verified: </Text>
            {data.remediation_text || `A verification retest was performed on ${data.retest_date}. All findings rated Critical and High have been remediated and confirmed closed.`}
          </Text>
        </View>
      )}

      {/* Point-in-time Disclaimer - Uniform body paragraph */}
      <Text style={styles.paragraph}>
        This attestation reflects the security posture of the in-scope systems <Text style={styles.boldText}>at the time of testing only</Text>. A penetration test is a point-in-time assessment and cannot guarantee that all vulnerabilities have been identified. This letter does not constitute a certification, warranty, or guarantee of security, and the full technical report remains confidential between the parties.
      </Text>

      {/* Contact Paragraph - Uniform body paragraph */}
      <Text style={[styles.paragraph, { marginBottom: 14 }]}>
        For verification of this attestation, please contact Capture The Bug at <Text style={styles.contactEmail}>hello@capturethebug.xyz</Text>.
      </Text>

      {/* Sign-off / Valid without physical signature */}
      <View style={styles.signoffBlock}>
        <Text style={styles.signoffTitle}>Issued by Capture The Bug Limited</Text>
        <Text style={styles.signoffSubtitle}>This is a system-generated attestation and is valid without a physical signature.</Text>
      </View>

      {/* Footer Line */}
      <View style={styles.footer}>
        <View style={styles.footerDivider} />
        <Text style={styles.footerText}>
          Capture The Bug Limited · CREST-certified penetration testing · Confidential
        </Text>
      </View>
    </Page>
  );
};

export default LetterOfAttestationPDF;
